import { QA_API_DOMAIN, fetchWithSupabaseAuth } from "@/api/classifier";
import type { ClinicalCode } from "@/clinical-codes/types/ClinicalCodeTypes";
import type { ClassifyFieldResult, CoMorbiditiesStepResult, OrderDiagnosisStepResult } from "@/types/classification";
import type { LlmModelIdentifier } from "@smartmed/llm-models";
import { useMemo } from "react";
const CLASSIFY_FIELD_URL = `${QA_API_DOMAIN}/testing/classify/field`;
const CLASSIFY_TEST_DATA_URL = `${QA_API_DOMAIN}/testing/classify/test-data`;
const CLASSIFY_ORDER_OF_DIAGNOSIS_URL = `${QA_API_DOMAIN}/testing/classify/ordering-of-diagnosis`;
const CLASSIFY_CO_MORBIDITIES_URL = `${QA_API_DOMAIN}/testing/classify/co-morbidities`;

const CLASSIFY_ENCOUNTER_URL = `${QA_API_DOMAIN}/testing/classify/encounter`;

interface ClassifyTestDataResponse {
  success: boolean;
  runId?: string;
  error?: string;
}

export function useClassifier() {
  const classifyField = useMemo(
    () =>
      async ({
        templateFieldId,
        medicalTestDataId,
      }: {
        templateFieldId: string;
        medicalTestDataId: string;
      }): Promise<ClassifyFieldResult> => {
        try {
          const response = await fetchWithSupabaseAuth(CLASSIFY_FIELD_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              templateFieldId,
              medicalTestDataId,
            }),
          });

          return response.json();
        } catch (error: unknown) {
          if (error instanceof Error) {
            throw new Error(`Failed to classify field: ${error.message}`);
          }
          throw new Error("Failed to classify field");
        }
      },
    [],
  );

  const classifyTestData = useMemo(
    () =>
      async (testDataId: string, llmModelId: LlmModelIdentifier): Promise<ClassifyTestDataResponse> => {
        try {
          const response = await fetchWithSupabaseAuth(CLASSIFY_TEST_DATA_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              testDataId,
              llmModelId,
            }),
          });

          if (!response.ok) {
            const error = (await response.json().catch((_) => null)) as {
              error: string;
            };
            if (error?.error) {
              throw new Error(error.error);
            }
            throw new Error(response.statusText);
          }

          return response.json();
        } catch (error) {
          console.error("Error classifying test data:", error);
          return {
            success: false,
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },
    [],
  );

  const classifyOrderDiagnosis = useMemo(
    () =>
      async ({
        testDataId,
        diagnoses,
        llmModelId,
      }: {
        testDataId: string;
        diagnoses: ClinicalCode[];
        llmModelId: LlmModelIdentifier | null;
      }): Promise<OrderDiagnosisStepResult> => {
        try {
          const response = await fetchWithSupabaseAuth(CLASSIFY_ORDER_OF_DIAGNOSIS_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              testDataId,
              diagnoses,
              llmModelId,
            }),
          });
          return response.json();
        } catch (error: unknown) {
          if (error instanceof Error) {
            throw new Error(`Failed to classify primary diagnosis: ${error.message}`);
          }
          throw new Error("Failed to classify primary diagnosis");
        }
      },
    [],
  );

  const classifyCoMorbidities = useMemo(
    () =>
      async ({
        medicalText,
        diagnoses,
        llmModelId,
      }: {
        medicalText: string | null;
        diagnoses: string[];
        llmModelId?: LlmModelIdentifier | null;
      }): Promise<CoMorbiditiesStepResult> => {
        try {
          const response = await fetchWithSupabaseAuth(CLASSIFY_CO_MORBIDITIES_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              medicalText,
              diagnoses,
              llmModelId,
            }),
          });
          return response.json();
        } catch (error: unknown) {
          if (error instanceof Error) {
            throw new Error(`Failed to classify co-morbidities: ${error.message}`);
          }
          throw new Error("Failed to classify co-morbidities");
        }
      },
    [],
  );

  const classifyEncounter = useMemo(
    () =>
      async ({
        encounterId,
        llmModelId,
      }: {
        encounterId: string;
        llmModelId?: LlmModelIdentifier;
      }): Promise<ClassifyTestDataResponse> => {
        try {
          const response = await fetchWithSupabaseAuth(CLASSIFY_ENCOUNTER_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              encounterId,
              llmModelId,
            }),
          });
          return response.json();
        } catch (error: unknown) {
          if (error instanceof Error) {
            throw new Error(`Failed to classify encounter: ${error.message}`);
          }
          throw new Error("Failed to classify encounter");
        }
      },
    [],
  );

  return {
    classifyEncounter,
    classifyField,
    classifyTestData,
    classifyOrderDiagnosis,
    classifyCoMorbidities,
  };
}
